import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Empty, Row, Tabs, Tooltip } from "antd";
import ColorThief from "colorthief";
import "./Personal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faChartLine,
  faCircleExclamation,
  faClockRotateLeft,
  faEllipsis,
  faMessage,
  faPaperclip,
  faPen,
  faUpload,
  faUserCheck,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import TooltipOptions from "../../Components/TooltipOptions/TooltipOptions";
import RecommentAndPolicy from "../../Components/RecommendArtistAndPolicy/RecommentAndPolicy";
import TrackOrTrackListItem from "./TrackOrTrackListItem/TrackOrTrackListItem";
import { useDispatch, useSelector } from "react-redux";
import {
  onOpenUpdate,
  onOpenUpload,
} from "../../Redux/UploadOrUpdateTrackSlice";
import UploadOrUpdateTrackModal from "./UploadOrUpdateTrackModal/UploadOrUpdateTrackModal";
import { setShowMore, setTracksAndTracklists } from "../../Redux/PersonalSlice";
import { onOpenCreateTracklist } from "../../Redux/CreateUpdateTracklistSlice";
import CreateOrUpdateTrackListModal from "./CreateOrUpdateTrackListModal/CreateOrUpdateTrackListModal";
import { setChatSpace } from "../../Redux/ChatSpaceSlice";
import { setPopup } from "../../Redux/PopupSlice";
import axios from "axios";
import { useParams } from "react-router-dom";
import CreateOrUpdatePlaylistModal from "./CreateOrUpdatePlaylistModal/CreateOrUpdatePlaylistModal";

function Personal() {
  const dispatch = useDispatch();
  const { userId } = useParams(); // id of any user coming from url
  const ownerInfo = JSON.parse(localStorage.getItem("userInfo") || "{}"); // owner
  const [userInfo, setUserInfo] = useState({});
  const isOwn = ownerInfo?.id === userId ? true : false;
  const [dataUpdated, setDataUpdated] = useState(false);
  // show more tooltip
  const personalTooltipRef = useRef(null);
  // title background
  const imgTitleRef = useRef(null);
  const [gradientColors, setGradientColors] = useState(["#ffffff", "#000000"]);
  const showMore = useSelector((state) => state.personal.openShowMore);
  // Upload modal
  const { open } = useSelector((state) => state.uploadOrUpdateTrack);
  // data
  const tabItems = [
    {
      label: <span className="profileTabLabel">Tracks</span>,
      key: "1",
      children: <ProfileTrackList isOwn={isOwn} type="track" />,
    },
    {
      label: <span className="profileTabLabel">Albums</span>,
      key: "2",
      children: <ProfileTrackList isOwn={isOwn} type="album" />,
    },
    {
      label: <span className="profileTabLabel">Playlists</span>,
      key: "3",
      children: <ProfileTrackList isOwn={isOwn} type="playlist" />,
    },
    {
      label: <span className="profileTabLabel">Reposts</span>,
      key: "4",
      children: <ProfileTrackList isOwn={isOwn} type="repost" />,
    },
  ];
  // test
  const [backgroundImg, setBackgroundImg] = useState(
    "https://images.pexels.com/photos/268533/pexels-photo-268533.jpeg?cs=srgb&dl=pexels-pixabay-268533.jpg&fm=jpg"
  );

  // fetch user info
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_SERVER_URL + `/user/get-by-id/${userId}`
        );
        setUserInfo(res.data);
      } catch (err) {
        console.error("Error fetching user info:", err);
      }
    };
    if (userId) {
      getUserInfo();
    }
  }, [userId]);

  // fetch tracks and tracklists
  useEffect(() => {
    const getTracks = async () => {
      try {
        const res = await axios.get(
          process.env.REACT_APP_SERVER_URL + `/track/get-by-user/${userId}`
        );
        dispatch(setTracksAndTracklists(res.data || []));
      } catch (err) {
        console.error("Error fetching tracks or tracklists:", err);
      }
    };
    if (userId) {
      getTracks();
    }
  }, [dataUpdated, userId]);

  // background color
  useEffect(() => {
    const image = imgTitleRef.current;
    const extractColors = (image) => {
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(image, 2); // Get top 2 colors
      if (palette) {
        const [color1, color2] = palette.map(
          ([r, g, b]) => `rgb(${r}, ${g}, ${b})`
        );
        setGradientColors([color1, color2]);
      }
    };
    if (image.complete) {
      extractColors(image);
    } else {
      image.onload = () => extractColors(image);
    }
  }, []);

  useEffect(() => {
    const handleBlurShowmore = (event) => {
      if (
        personalTooltipRef.current &&
        !personalTooltipRef.current.contains(event.target)
      ) {
        dispatch(setShowMore(false));
      }
    };
    document.addEventListener("mousedown", handleBlurShowmore);
    return () => {
      document.removeEventListener("mousedown", handleBlurShowmore);
    };
  }, []);

  const handleShowmore = () => {
    dispatch(setShowMore(!showMore));
  };

  const handleOpenUploadModal = () => {
    dispatch(onOpenUpload());
  };

  const handleOpenChatSpace = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/conversation/start`,
        {
          partnerId: userId,
          userId: ownerInfo?.id,
        }
      );

      // Use the dispatch from component scope
      dispatch(setChatSpace({ open: "message", info: response.data.data }));
      dispatch(setPopup(null));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {open && <UploadOrUpdateTrackModal setDataUpdated={setDataUpdated} />}
      <Col
        className="profileTitleHeader"
        span={18}
        offset={3}
        style={{
          backgroundImage: backgroundImg
            ? `url(${backgroundImg})`
            : `linear-gradient(to bottom right, ${gradientColors[0]}, ${gradientColors[1]})`,
        }}
      >
        <Row>
          <Col span={6}>
            <img
              className="profileTitleImg"
              ref={imgTitleRef}
              size={150}
              crossOrigin="anonymous"
              src={userInfo?.avatar || "/blankAvatarImg.jpg"}
            />
          </Col>
          <Col className="profileNameAndBio" span={16}>
            <div>
              <span className="profileName">{userInfo?.username}</span>
              {/* <span className="profileCountry">Den vau</span> */}
            </div>
            <span className="profileBio">
              {userInfo?.bio ||
               "Một người đam mê âm nhạc, luôn tìm kiếm những giai điệu mới để khám phá và chia sẻ. Dù là sáng tạo hay thưởng thức, âm nhạc luôn là nguồn cảm hứng, kết nối cảm xúc và mở ra những trải nghiệm đầy ý nghĩa."}
            </span>
          </Col>
        </Row>
      </Col>
      <Col className="profileButtons" span={18} offset={3}>
        {isOwn ? (
          <>
            <Button size="small" onClick={handleOpenUploadModal}>
              <FontAwesomeIcon icon={faUpload} />
              Đăng tải track
            </Button>
            <Button size="small" disabled>
              <FontAwesomeIcon icon={faPaperclip} />
              Sao chép link
            </Button>
            <Button size="small" disabled>
              <FontAwesomeIcon icon={faPen} />
              Chỉnh sửa thông tin
            </Button>
          </>
        ) : (
          <>
            <Button size="small" disabled>
              {<FontAwesomeIcon icon={faUserPlus} /> || (
                <FontAwesomeIcon icon={faUserCheck} />
              )}
              Theo dõi
            </Button>
            <Button
              size="small"
              onClick={() => {
                handleOpenChatSpace();
              }}
            >
              <FontAwesomeIcon icon={faMessage} />
              Trò chuyện
            </Button>
            <Button size="small" disabled>
              <FontAwesomeIcon icon={faPaperclip} />
              Sao chép link
            </Button>
          </>
        )}
        <div ref={personalTooltipRef}>
          <Tooltip
            placement="bottomRight"
            color="rgba(245, 245, 245)"
            overlayStyle={{ width: "200px" }}
            open={showMore}
            title={<MoreOptions isOwn={isOwn} />}
          >
            <Button size="small" onClick={handleShowmore}>
              <FontAwesomeIcon icon={faEllipsis} />
            </Button>
          </Tooltip>
        </div>
      </Col>
      <Col span={12} offset={3}>
        <Tabs defaultActiveKey="1" items={tabItems} />
      </Col>
      <Col span={1} className="borderSplitWrapper">
        <div className="contentBorderSplit"></div>
      </Col>
      <Col span={5}>
        <RecommentAndPolicy type={"inProfilePage"} />
      </Col>
    </>
  );
}

function ProfileTrackList({ type = "track", isOwn = false }) {
  const dispatch = useDispatch();
  const { openList, listType } = useSelector(
    (state) => state.createUpdateTracklist
  );
  const tracksAndTracklists = useSelector(
    (state) => state.personal.tracksAndTracklists
  );
  const [filteredList, setFilteredList] = useState([]);

  useEffect(() => {
    if (tracksAndTracklists) {
      const filtered = tracksAndTracklists.filter((trackOrTracklist) => {
        return trackOrTracklist.category === type;
      });
      setFilteredList(filtered);
    }
  }, [type, tracksAndTracklists]);

  const handleShowCreateUpdateTracklistModal = () => {
    dispatch(onOpenCreateTracklist({ listType: type }));
  };

  return (
    <>
      {openList &&
        (listType === "album" ? (
          <CreateOrUpdateTrackListModal />
        ) : listType === "playlist" ? (
          <CreateOrUpdatePlaylistModal />
        ) : null)}

      {isOwn && type === "album" && (
        <Row>
          <Col span={24} className="showCreateUpdateTracklistBtnWrapper">
            <Button
              disabled
              onClick={handleShowCreateUpdateTracklistModal}
              className="showCreateUpdateTracklistBtn"
            >
              Tạo {type}
            </Button>
          </Col>
        </Row>
      )}
      {filteredList.length > 0 ? (
        filteredList.map((trackOrTracklist) => (
          <TrackOrTrackListItem
            trackType={type}
            isOwn={isOwn}
            key={trackOrTracklist._id}
            trackOrTracklist={trackOrTracklist}
          />
        ))
      ) : (
        <Empty description={`Hiện không có ${type} nào`} />
      )}
    </>
  );
}

function MoreOptions({ isOwn }) {
  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <Row style={{ color: "black" }}>
        {isOwn ? (
          <>
            <TooltipOptions
              icon={faClockRotateLeft}
              title="Lịch sử nghe"
              endSecion={false}
            />
            {/* <TooltipOptions
              icon={faClockRotateLeft}
              title="Lịch sử hoạt động"
              endSecion={true}
            /> */}
            <TooltipOptions
              icon={faChartLine}
              title="Thống kê hàng tháng"
              endSecion={false}
            />
          </>
        ) : (
          <>
            <TooltipOptions icon={faBan} title="Chặn" endSecion={true} />
            <TooltipOptions
              icon={faCircleExclamation}
              title="Báo cáo"
              endSecion={false}
            />
          </>
        )}
      </Row>
    </div>
  );
}

export default Personal;
